/**
 * PhotoGallery -- displays photo thumbnails in a responsive grid.
 *
 * M81 migration: replaced URL.createObjectURL on the legacy photo blob with
 * PhotoThumbnail which uses usePhotoSrc for on-demand hydration. PhotoLightbox
 * now resolves bytes via usePhotoSrc internally (getUrl not required).
 * M83 (ADR-044): usePhotoSrc reconstructs the Blob from photo.blobBytes —
 * IDB stores ArrayBuffer, never Blob, to avoid iOS Safari WebKit corruption.
 *
 * Each thumbnail wrapper renders data-photo-state for semantic test assertions
 * (RULE-096), surfaced through PhotoThumbnail.
 */

import { useState } from 'react';
import { X } from 'lucide-react';

import { PhotoLightbox } from './PhotoLightbox';
import { PhotoThumbnail } from './PhotoThumbnail';
import type { PhotoRecord } from '@/types/photo';

export interface PhotoGalleryProps {
  photos: PhotoRecord[];
  onDelete?: (photoId: string) => void;
  editable?: boolean;
  /** Optional callback to provide a context label for the lightbox (e.g. 'North Blind · Morning') */
  getSitContext?: (photo: PhotoRecord) => string | undefined;
}

export function PhotoGallery({ photos, onDelete, editable = false, getSitContext }: PhotoGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (photos.length === 0) return null;

  return (
    <>
      <div
        className="ph-no-capture grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
        data-testid="photo-gallery"
      >
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            className="relative aspect-square rounded-md overflow-hidden bg-[var(--color-elevated)] border border-[var(--color-border)]"
          >
            <PhotoThumbnail
              photoId={photo.id}
              alt={photo.originalFilename ?? `Photo ${index + 1}`}
              onClick={() => setLightboxIndex(index)}
              aria-label={`View photo ${index + 1}`}
              data-testid={`photo-thumb-${photo.id}`}
            />

            {editable && onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(photo.id);
                }}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                aria-label={`Delete photo ${index + 1}`}
                data-testid={`delete-photo-${photo.id}`}
              >
                <X size={10} strokeWidth={3} />
              </button>
            )}
          </div>
        ))}
      </div>

      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          sitContext={
            getSitContext && lightboxIndex !== null && photos[lightboxIndex]
              ? getSitContext(photos[lightboxIndex])
              : undefined
          }
        />
      )}
    </>
  );
}
